import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with error handling
const stripePromise = (() => {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
    (process.env.NODE_ENV !== 'production' ? 'pk_live_51SDTcT5hGyJbtDebcdN8V5PUjdmbC6TMlN9VNXDxbH8hOE0sPleDEn90vkhA9fiyDsDG5OvQdYBbp7SvTKzV71JW00N6c3EYuj' : 'pk_live_51SDTcT5hGyJbtDebcdN8V5PUjdmbC6TMlN9VNXDxbH8hOE0sPleDEn90vkhA9fiyDsDG5OvQdYBbp7SvTKzV71JW00N6c3EYuj');
  
  if (!publishableKey) {
    console.warn('⚠️ REACT_APP_STRIPE_PUBLISHABLE_KEY not set. Stripe features will be disabled.');
    return null;
  }
  
  if (!publishableKey.startsWith('pk_')) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Invalid Stripe publishable key format. Key should start with "pk_".');
    } else {
      console.warn('⚠️ Invalid Stripe publishable key format in development. Stripe features will be disabled.');
      return null;
    }
  }
  
  return loadStripe(publishableKey);
})();

export const createCheckoutSession = async (priceId, userId, userEmail, billingPeriod = 'one-time') => {
  try {
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        userId,
        userEmail,
        billingPeriod,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response not OK:', response.status, errorText);
      throw new Error(`Failed to create checkout session: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('Checkout session response:', responseData);
    
    if (!responseData.sessionId) {
      throw new Error('No session ID in response');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export const redirectToCheckout = async (priceId, userId, userEmail, billingPeriod = 'one-time') => {
  try {
    if (!stripePromise) {
      console.warn('⚠️ Stripe not configured. Checkout will be skipped.');
      return;
    }
    
    const sessionData = await createCheckoutSession(priceId, userId, userEmail, billingPeriod);
    
    // Use the URL from the backend response
    window.location.href = sessionData.url;
    
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

// Price IDs for different plans (these would be set up in your Stripe dashboard)
export const PRICE_IDS = {
  'small-pack': process.env.REACT_APP_STRIPE_SMALL_PACK_PRICE_ID || 'price_1SIrOr5hGyJbtDebvuww5tQZ', // 5 Credits - €2
  'medium-pack': process.env.REACT_APP_STRIPE_MEDIUM_PACK_PRICE_ID || 'price_1SIrbp5hGyJbtDebSiylisyO', // 15 Credits - €5
  'big-pack': process.env.REACT_APP_STRIPE_BIG_PACK_PRICE_ID || 'price_1SIriy5hGyJbtDebTDevR5rI', // 50 Credits - €10
  'premium-pack': process.env.REACT_APP_STRIPE_PREMIUM_PACK_PRICE_ID || 'price_1SIrkq5hGyJbtDebHZD6cr6z', // 200 Credits - €20
  'enterprise-pack': process.env.REACT_APP_STRIPE_ENTERPRISE_PACK_PRICE_ID || 'price_1SIrlK5hGyJbtDebWinPjuE2', // 800 Credits - €40
  'monthly-basic': process.env.REACT_APP_STRIPE_MONTHLY_BASIC_PRICE_ID || 'price_1SDWye5hGyJbtDebjx5Gaz8I',
  'monthly-pro': process.env.REACT_APP_STRIPE_MONTHLY_PRO_PRICE_ID || 'price_1SDa2A5hGyJbtDebRs6zcSRO',
  'monthly-unlimited': process.env.REACT_APP_STRIPE_MONTHLY_UNLIMITED_PRICE_ID || 'price_1SDWyx5hGyJbtDeb2It21CTy',
};

// Plan configurations
export const PLAN_CONFIGS = {
  'small-pack': {
    mockups: 5, // 5 Credits - €2
    isSubscription: false,
    priceId: PRICE_IDS['small-pack']
  },
  'medium-pack': {
    mockups: 15, // 15 Credits - €5
    isSubscription: false,
    priceId: PRICE_IDS['medium-pack']
  },
  'big-pack': {
    mockups: 50, // 50 Credits - €10
    isSubscription: false,
    priceId: PRICE_IDS['big-pack']
  },
  'premium-pack': {
    mockups: 200, // 200 Credits - €20
    isSubscription: false,
    priceId: PRICE_IDS['premium-pack']
  },
  'enterprise-pack': {
    mockups: 800, // 800 Credits - €40
    isSubscription: false,
    priceId: PRICE_IDS['enterprise-pack']
  },
  'monthly-basic': {
    mockups: 50,
    isSubscription: true,
    priceId: PRICE_IDS['monthly-basic']
  },
  'monthly-pro': {
    mockups: 300,
    isSubscription: true,
    priceId: PRICE_IDS['monthly-pro']
  },
  'monthly-unlimited': {
    mockups: -1, // -1 for unlimited
    isSubscription: true,
    priceId: PRICE_IDS['monthly-unlimited']
  }
};

export default stripePromise;
